import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { LeaderboardEntry } from '../types/leaderboard';

export function useSupabaseLeaderboard() {
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchLeaderboard();

    const channel = supabase
      .channel('leaderboard_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'leaderboard'
        },
        () => {
          fetchLeaderboard();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchLeaderboard = async () => {
    try {
      const { data, error } = await supabase
        .from('leaderboard')
        .select('*')
        .eq('is_ai', false) // Only select non-AI players
        .order('points', { ascending: false });

      if (error) throw error;

      const formattedEntries: LeaderboardEntry[] = data.map((entry, index) => ({
        username: entry.username,
        walletAddress: entry.wallet_address,
        points: entry.points,
        rank: index + 1,
        lastUpdated: entry.last_updated
      }));

      setEntries(formattedEntries);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch leaderboard');
    } finally {
      setLoading(false);
    }
  };

  const updatePoints = async (username: string, walletAddress: string, points: number) => {
    try {
      const { error } = await supabase
        .from('leaderboard')
        .upsert({
          username,
          wallet_address: walletAddress,
          points,
          last_updated: new Date().toISOString()
        }, {
          onConflict: 'wallet_address'
        });

      if (error) throw error;
      await fetchLeaderboard();
    } catch (err) {
      console.error('Failed to update points:', err);
      throw err;
    }
  };

  const addUser = async (username: string, walletAddress: string, points: number) => {
    try {
      const { error } = await supabase
        .from('leaderboard')
        .upsert({
          username,
          wallet_address: walletAddress,
          points,
          is_ai: false, // Ensure new users are marked as non-AI
          last_updated: new Date().toISOString()
        }, {
          onConflict: 'wallet_address'
        });

      if (error) throw error;
      await fetchLeaderboard();
    } catch (err) {
      console.error('Failed to add user:', err);
      throw err;
    }
  };

  return {
    entries,
    loading,
    error,
    updatePoints,
    addUser
  };
}